import { useParams, Link, Navigate } from 'react-router-dom'
import { ArrowLeft, Clock, Tag, ArrowRight } from 'lucide-react'
import SEO from '../components/SEO'
import Button from '../components/ui/Button'
import { getPostBySlug, BLOG_POSTS } from '../data/blogPosts'

function ContentBlock({ block }) {
  switch (block.type) {
    case 'p':
      return <p className="text-[var(--ink-soft)] leading-relaxed mb-5">{block.text}</p>
    case 'h2':
      return <h2 className="text-2xl font-display font-bold text-ink mt-10 mb-4">{block.text}</h2>
    case 'ul':
      return (
        <ul className="space-y-2 mb-5 pl-1">
          {block.items.map((item, i) => (
            <li key={i} className="flex items-start gap-3 text-[var(--ink-soft)] leading-relaxed">
              <span className="w-1.5 h-1.5 rounded-full mt-2.5 flex-shrink-0" style={{ background: 'var(--accent)' }} />
              {item}
            </li>
          ))}
        </ul>
      )
    case 'img':
      return (
        <figure className="my-8">
          <img
            src={block.src}
            alt={block.alt}
            className="w-full rounded border border-theme"
            loading="lazy"
            width={1200}
            height={630}
            style={{ aspectRatio: '1200 / 630', objectFit: 'cover' }}
          />
          {block.caption && (
            <figcaption className="text-xs text-[var(--ink-soft)] mt-2.5">{block.caption}</figcaption>
          )}
        </figure>
      )
    case 'cta':
      return (
        <div className="my-10 rounded p-6 md:p-8" style={{ background: 'color-mix(in srgb, var(--accent) 5%, transparent)', border: '1px solid color-mix(in srgb, var(--accent) 20%, transparent)' }}>
          <h3 className="text-xl font-display font-bold text-ink mb-2">{block.heading}</h3>
          <p className="text-[var(--ink-soft)] mb-5">{block.body}</p>
          <div className="flex flex-wrap gap-3">
            <Link to={block.primary.to}>
              <Button variant="primary">{block.primary.label} <ArrowRight size={14} /></Button>
            </Link>
            {block.secondary && (
              <Link to={block.secondary.to}>
                <Button variant="ghost">{block.secondary.label}</Button>
              </Link>
            )}
          </div>
        </div>
      )
    default:
      return null
  }
}

export default function BlogPost() {
  const { slug } = useParams()
  const post = getPostBySlug(slug)

  if (!post) return <Navigate to="/blog" replace />

  const schema = [
    {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: post.title,
      ...(post.image ? { image: [post.image] } : {}),
      datePublished: post.date,
      dateModified: post.date,
      author: { '@type': 'Organization', name: 'Tvantra' },
      publisher: {
        '@type': 'Organization',
        name: 'MySchoolPortal',
        logo: { '@type': 'ImageObject', url: 'https://myschoolportal.xyz/favicon.svg' },
      },
      description: post.excerpt,
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': `https://myschoolportal.xyz/blog/${post.slug}`,
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://myschoolportal.xyz' },
        { '@type': 'ListItem', position: 2, name: 'Blog', item: 'https://myschoolportal.xyz/blog' },
        { '@type': 'ListItem', position: 3, name: post.title, item: `https://myschoolportal.xyz/blog/${post.slug}` },
      ],
    },
  ]

  return (
    <div className="pt-8 pb-16">
      <SEO
        title={post.title}
        description={post.excerpt}
        path={`/blog/${post.slug}`}
        schema={schema}
      />

      <div className="px-4 max-w-3xl mx-auto">
        {/* Back */}
        <Link
          to="/blog"
          className="inline-flex items-center gap-2 text-sm text-[var(--ink-soft)] hover:text-ink mb-10"
        >
          <ArrowLeft size={16} /> Back to Blog
        </Link>

        {/* Header */}
        <header className="mb-10">
          <div className="flex flex-wrap items-center gap-3 mb-5">
            <span className="flex items-center gap-1.5 text-xs font-semibold text-accent">
              <Tag size={11} /> {post.category}
            </span>
            <span className="flex items-center gap-1.5 text-xs text-[var(--ink-soft)]">
              <Clock size={11} /> {post.readingTime}
            </span>
            <time className="text-xs text-[var(--ink-soft)]">
              {new Date(post.date).toLocaleDateString('en-GH', { year: 'numeric', month: 'long', day: 'numeric' })}
            </time>
          </div>

          <h1 className="text-3xl md:text-4xl lg:text-5xl font-display font-extrabold text-ink leading-tight mb-6">
            {post.title}
          </h1>

          <p className="text-lg text-[var(--ink-soft)] leading-relaxed pl-4" style={{ borderLeft: '4px solid var(--accent)' }}>
            {post.excerpt}
          </p>
        </header>

        {post.image && (
          <img
            src={post.image}
            alt={post.imageAlt || ''}
            className="w-full rounded border border-theme mb-10"
            width={1200}
            height={630}
            style={{ aspectRatio: '1200 / 630', objectFit: 'cover' }}
          />
        )}

        <div className="border-t border-theme mb-10" />

        {/* Content */}
        <div>
          {post.content.map((block, i) => (
            <ContentBlock key={i} block={block} />
          ))}
        </div>

        {/* More posts */}
        {BLOG_POSTS.filter(p => p.slug !== post.slug).length > 0 && (
          <div className="mt-16 pt-10 border-t border-theme">
            <h3 className="text-lg font-display font-bold text-ink mb-6">More articles</h3>
            <div className="space-y-4">
              {BLOG_POSTS.filter(p => p.slug !== post.slug).map(p => (
                <Link
                  key={p.slug}
                  to={`/blog/${p.slug}`}
                  className="group flex items-start gap-4 p-4 rounded border border-theme hover:border-theme-strong transition-colors duration-150"
                >
                  <div className="flex-1">
                    <p className="text-xs font-semibold mb-1 text-accent">{p.category}</p>
                    <p className="text-sm font-bold text-ink leading-snug">
                      {p.title}
                    </p>
                  </div>
                  <ArrowRight size={14} className="text-[var(--ink-soft)] mt-1 flex-shrink-0" />
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
